import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatasetStore, effectiveDashboardName } from '@/store/dataset-store';
import { useFiltersStore } from '@/store/filters-store';
import { sanitizeFileName } from '@/lib/sanitize-filename';
import { useWidgetsStore } from '@/store/widgets-store';
import { useTableVisibilityStore } from '@/store/table-vis-store';
import { useModeStore } from '@/store/mode-store';
import { useCompareConfigStore } from '@/store/compare-config-store';
import { getQueryWorker } from '@/engine/worker-client';
import { buildSpec, aggregationLabelKey } from '@/engine/widget-spec';
import { formatPeriod } from '@/features/compare/format-period';
import { listCharts } from './chart-registry';
import type {
  Aggregation,
  ChartImage,
  ExportRequest,
  WidgetExportSpec,
} from '@/types/query';
import type { WidgetConfig, WidgetKind } from '@/types/widget';
import type { CompareConfigEntry } from '@/persistence/config-db';
import type { TFunction } from 'i18next';

/**
 * Excel export trigger. Two visual variants:
 *  - `sidebar`  — full-width dashed button, inline error message.
 *  - `compact`  — chip-sized button for placement in widget title bars
 *                 (errors fall back to `window.alert`).
 *
 * Builds a 3-sheet workbook:
 *   1. *Veri* — the dashboard table's filtered rows, only the columns the
 *      user has left visible.
 *   2. *Grafik Verisi* — per-chart aggregated data block (one block per
 *      bar/line/pie widget).
 *   3. *Grafikler* — PNG snapshots of each chart (taken on the main thread
 *      because ECharts lives in the DOM), embedded as images.
 */
type Variant = 'sidebar' | 'compact';

const WARN_THRESHOLD = 500_000;
const HARD_LIMIT = 1_000_000;
/**
 * Widget kinds that appear on sheet 2 ("Grafik Verisi"). Includes
 * `compare-table` so its winners/losers rows ride along on the export
 * even though the renderer treats them differently from a normal
 * groupBy-by-metric block.
 */
const EXPORTABLE_KINDS = new Set<WidgetKind>([
  'bar',
  'line',
  'pie',
  'compare-table',
]);

export function ExcelExportButton({
  variant = 'sidebar',
}: {
  variant?: Variant;
}): JSX.Element | null {
  const { t } = useTranslation();
  const activeId = useDatasetStore((s) => s.activeDatasetId);
  const datasets = useDatasetStore((s) => s.datasets);
  const filters = useFiltersStore((s) => s.filters);
  const mode = useModeStore((s) => s.mode);
  const compareConfig = useCompareConfigStore((s) => s.config);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entry = activeId ? datasets.get(activeId) : undefined;
  if (!entry || !activeId) return null;

  async function runExport(): Promise<void> {
    if (!entry || !activeId) return;
    setError(null);
    setBusy(true);
    try {
      const transforms = entry.valueGroupings;
      const baseReq: ExportRequest = {
        datasetId: activeId,
        filters,
        ...(transforms && Object.keys(transforms).length > 0
          ? { transforms }
          : {}),
      };

      const api = getQueryWorker();
      const { rowCount } = await api.getExportRowCount(baseReq);

      if (rowCount === 0) {
        showError(t('errors.noRowsToExport'));
        return;
      }
      if (rowCount > HARD_LIMIT) {
        showError(
          t('export.excel.tooManyRows', {
            rows: rowCount.toLocaleString(),
            limit: HARD_LIMIT.toLocaleString(),
          }),
        );
        return;
      }
      if (rowCount > WARN_THRESHOLD) {
        const ok = window.confirm(
          t('export.excel.confirmLarge', {
            rows: rowCount.toLocaleString(),
          }),
        );
        if (!ok) return;
      }

      const visibleColumns = pickVisibleColumns(activeId, entry.columns.map((c) => c.name));
      const compareForExport =
        mode === 'compare' && compareConfig ? compareConfig : null;
      const widgetSpecs = buildWidgetExportSpecs(
        activeId,
        compareForExport,
        t,
      );
      const chartImages = snapshotChartImages();

      const req: ExportRequest = {
        ...baseReq,
        visibleColumns,
        widgets: widgetSpecs,
        chartImages,
        i18n: {
          sheetData: t('export.excel.sheetData'),
          sheetChartData: t('export.excel.sheetChartData'),
          sheetCharts: t('export.excel.sheetCharts'),
          noChartWidgets: t('export.excel.noChartWidgets'),
          periodCol: t('export.excel.periodCol'),
          noChartImages: t('export.excel.noChartImages'),
          breakdownLabel: t('export.excel.breakdownLabel'),
          compareTableWinnersHeader: t('compare.table.winnersHeader', {
            n: '__N__',
          }),
          compareTableLosersHeader: t('compare.table.losersHeader', {
            n: '__N__',
          }),
          compareTableCategoryCol: t('compare.table.categoryCol'),
          compareTableDiffCol: t('compare.table.diffCol'),
          compareTablePercentCol: t('compare.table.percentCol'),
        },
      };

      const { buffer } = await api.exportFilteredRows(req);
      triggerDownload(buffer, effectiveDashboardName(entry));
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('export.unknownError');
      showError(t('export.excel.failed', { message: msg }));
    } finally {
      setBusy(false);
    }
  }

  function showError(msg: string): void {
    if (variant === 'compact') {
      window.alert(msg);
    } else {
      setError(msg);
    }
  }

  if (variant === 'compact') {
    return (
      <button
        type="button"
        onClick={() => void runExport()}
        disabled={busy}
        className="text-xs px-2 py-0.5 rounded hover:opacity-80 flex items-center gap-1 disabled:opacity-50"
        style={{
          color: 'var(--color-muted)',
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
        }}
        title={t('export.excel.compactTooltip')}
      >
        {busy ? '…' : t('export.excel.compactLabel')}
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => void runExport()}
        disabled={busy}
        className="w-full text-xs px-2.5 py-2 rounded-md hover:opacity-80 text-left disabled:opacity-50"
        style={{
          color: 'var(--color-fg)',
          background: 'var(--color-hover)',
          border: '1px solid var(--color-border)',
        }}
      >
        {busy ? t('export.excel.busy') : t('export.excel.label')}
      </button>
      {error && (
        <span
          className="text-[11px] px-1"
          style={{ color: 'var(--color-danger, #ef4444)' }}
          role="alert"
        >
          {error}
        </span>
      )}
    </>
  );
}

/**
 * Return the dataset's column names in display order with hidden columns
 * (per the table visibility store) filtered out.
 */
function pickVisibleColumns(datasetId: string, all: string[]): string[] {
  const vis = useTableVisibilityStore.getState().visibility[datasetId] ?? {};
  return all.filter((name) => vis[name] !== false);
}

/**
 * Translate the dataset's chart widgets (bar/line/pie) into the spec shape
 * the worker uses for sheet 2. KPI widgets are skipped — they have no
 * group-by and don't belong on the chart-data sheet.
 */
function buildWidgetExportSpecs(
  datasetId: string,
  compareConfig: CompareConfigEntry | null,
  t: TFunction,
): WidgetExportSpec[] {
  const widgetsState = useWidgetsStore.getState();
  if (widgetsState.datasetId !== datasetId) return [];

  const datasetEntry = useDatasetStore.getState().datasets.get(datasetId);
  if (!datasetEntry) return [];
  const filters = useFiltersStore.getState().filters;

  const out: WidgetExportSpec[] = [];
  for (const w of widgetsState.widgets) {
    if (!EXPORTABLE_KINDS.has(w.kind) || !w.groupBy) continue;
    const groupByColumnSqlType = datasetEntry.columns.find(
      (c) => c.name === w.groupBy,
    )?.sqlType;
    const spec = buildSpec(
      w,
      filters,
      groupByColumnSqlType,
      datasetEntry.valueGroupings,
    );
    const item: WidgetExportSpec = {
      id: w.id,
      title: w.title,
      kind: w.kind as 'bar' | 'line' | 'pie' | 'compare-table',
      spec,
      seriesNames: buildSeriesNames(w, t),
      groupByColumn: w.groupBy,
    };
    if (groupByColumnSqlType) item.groupByColumnSqlType = groupByColumnSqlType;
    if (w.kind === 'compare-table') {
      item.compareTableLimit = w.limit ?? 10;
    }
    // Mirror the chart's spec-builder gate: breakdown is only active when
    // the user hasn't also configured extraMetrics (mutually exclusive).
    if (w.breakdownBy && !w.extraMetrics?.length) {
      item.breakdownBy = w.breakdownBy;
      if (w.breakdownLimit != null) item.breakdownLimit = w.breakdownLimit;
      if (w.limit != null) item.breakdownPrimaryLimit = w.limit;
    }
    if (compareConfig) {
      item.compare = {
        config: {
          dateColumn: compareConfig.dateColumn,
          granularity: compareConfig.granularity,
          periodA: compareConfig.periodA,
          periodB: compareConfig.periodB,
        },
        labelA: formatPeriod(t, compareConfig.granularity, compareConfig.periodA),
        labelB: formatPeriod(t, compareConfig.granularity, compareConfig.periodB),
      };
    }
    out.push(item);
  }
  return out;
}

function buildSeriesNames(w: WidgetConfig, t: TFunction): Record<string, string> {
  const labelOf = (m: {
    aggregation: Aggregation;
    column: string;
    label?: string;
  }): string =>
    m.label?.trim() || `${t(aggregationLabelKey(m.aggregation))} ${m.column}`;
  const out: Record<string, string> = { metric: labelOf(w.metric) };
  w.extraMetrics?.forEach((em, i) => {
    out[`metric_${i + 1}`] = labelOf(em);
  });
  return out;
}

/**
 * Snapshot every registered ECharts instance to a base64 PNG. Charts that
 * haven't mounted yet (e.g. inside a still-loading widget) are skipped.
 */
function snapshotChartImages(): Record<string, ChartImage> {
  const bg = readCssVar('--color-bg', '#ffffff');
  const out: Record<string, ChartImage> = {};
  for (const c of listCharts()) {
    const inst = c.getInstance();
    if (!inst) continue;
    const dataUrl = inst.getDataURL({
      type: 'png',
      pixelRatio: 1.5,
      backgroundColor: bg,
    });
    out[c.id] = {
      dataUrl,
      width: inst.getWidth(),
      height: inst.getHeight(),
    };
  }
  return out;
}

function readCssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v.length > 0 ? v : fallback;
}

function triggerDownload(buffer: ArrayBuffer, dashboardName: string): void {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const base = sanitizeFileName(dashboardName) || 'dashboard';
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${base}-${stamp}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
